import styled from '@emotion/styled';
import { LegendInfoIcon } from '@resistapp/client/components/info-icons/legend-info-icon';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { MetricTitleWithDisclaimer } from '../../shared/overview-metric-title';
import { theme } from '../../shared/theme';

interface Props {
  timeLegend: React.ReactNode | string;
  indicators: React.ReactNode;
  indexRange: React.ReactNode;
  inline?: boolean;
  showInfoIcon?: boolean;
  enableContextualTip?: boolean;
}

export function BaseMapLegend({
  timeLegend,
  indicators,
  indexRange,
  inline = false,
  showInfoIcon = false,
  enableContextualTip = false,
}: Props) {
  const { metricMode, activeOverviewConfiguration } = useOverviewContext();
  const { queryFilters } = useSampleDataContext();

  return (
    <MapLegendContainer id={inline ? 'inline-map-legend-container' : 'map-legend-container'} inline={inline}>
      {showInfoIcon && (
        <InlineLegendInfoIconContainer>
          <LegendInfoIcon
            enableContextualTip={enableContextualTip}
            metricMode={metricMode}
            infoIconProps={{
              selectedGeneGroup: queryFilters.filters.selectedTargets,
            }}
            activeOverviewConfiguration={activeOverviewConfiguration}
          />
        </InlineLegendInfoIconContainer>
      )}
      <MapLegendTitle>
        <MetricTitleWithDisclaimer />, {timeLegend}
      </MapLegendTitle>
      <MapIndicatorsContainer>{indicators}</MapIndicatorsContainer>
      <MapIndexRange>{indexRange}</MapIndexRange>
    </MapLegendContainer>
  );
}

const mapLegendBottomMargin = 25;
const mapLegendHeight = 74;
const MapLegendContainer = styled.div<{ inline?: boolean }>`
  position: ${props => (props.inline ? 'relative' : 'absolute')};
  bottom: ${props => (props.inline ? 'auto' : `${mapLegendBottomMargin}px`)};
  right: ${props => (props.inline ? 'auto' : '10px')};
  width: 382px;
  max-width: ${props => (props.inline ? '100%' : '80dvw')};
  height: ${mapLegendHeight}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: ${props => (props.inline ? 'auto' : theme.zIndexes.overviewMapControls)};
  padding: ${theme.spacing[2]};
  border-radius: ${theme.borders.radius.small};
  border: ${props => (props.inline ? `1px solid ${theme.colors.neutral200}` : 'none')};
  background-color: white;
  filter: ${props => (props.inline ? 'none' : theme.shadows.normal.filter)};
  font-weight: 500;
`;

const MapLegendTitle = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 8px;
  padding-right: 24px;
  ${/* this right padding fixes mobile view, where the info icon intersects the text */ ''}
  color: ${theme.colors.neutral700};
  font-size: 13px;
  line-height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const MapIndicatorsContainer = styled.div`
  width: 100%;
  height: 24px;
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing[0.5]}; // This was in resistance-index-map-legend.tsx, but not in risk-score-map-legend.tsx
`;
const MapIndexRange = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  color: ${theme.colors.neutral700};
  text-align: center;
  font-size: 13px;
  line-height: 100%;
  margin-top: 4px; // This was in risk-score-map-legend.tsx, but not in resistance-index-map-legend.tsx
`;

const InlineLegendInfoIconContainer = styled.div`
  position: absolute;
  top: ${theme.spacing[1]};
  right: ${theme.spacing[1]};
  z-index: 1;
  font-size: 15px;
  line-height: 1;
`;
